import { useEffect, useMemo, useState } from "react";
import { LiveKitRoom, RoomAudioRenderer, ParticipantTile, useTracks } from "@livekit/components-react";
import { Track, VideoQuality } from "livekit-client";
import { Swords } from "lucide-react";
import { api } from "@/lib/api";

/**
 * BattleSplitLayout — OVERLAY-ONLY during an active 1v1 battle.
 *
 * Iter67 — Fix A + left/right 50/50 layout.
 *
 * Layout (portrait AND landscape — same on both):
 *
 *      ┌─────────────────┬─────────────────┐
 *      │                 │                 │
 *      │   Host A        │   Host B        │
 *      │   (LEFT half,   │   (RIGHT half,  │
 *      │   50% width)    │   50% width)    │
 *      │                 │                 │
 *      └─────────────────┴─────────────────┘
 *                     ↑
 *          thin center divider with tiny VS badge
 *
 * This component does NOT render `<LiveRoomStage>`. That component lives one
 * level up in `LiveRoom.jsx` and stays mounted continuously through battle
 * start/end so the LiveKit publisher isn't torn down (Fix A — see the P0
 * crash RCA against Prod telemetry). The current viewer's own half is
 * filled by that stable LiveRoomStage; this overlay only draws:
 *
 *   1. ONE `BattleOpponentPane` — a hidden, subscribe-only LiveKit
 *      connection to the OTHER host's room, sized to fill the opposite
 *      half of the viewport.
 *   2. A thin vertical center line + tiny VS badge as the visual seam
 *      between the two halves. Score / timer / handles / winner UX is
 *      already rendered by `<BattleBanner>` at the top — we do NOT
 *      double-render that here to keep faces unobstructed.
 *
 * ARCHITECTURE NOTE: no room merging, scoring / gifts / commissions /
 * winner logic remain unchanged (per user directive).
 */

const PEEK_ROOM_OPTIONS = { adaptiveStream: true, dynacast: true };

export default function BattleSplitLayout({ stream, battle, currentUserId, audioBridgeReady = false }) {
  // Which side of the split does the viewer's own stream correspond to?
  const myStreamIsHost = stream?.id === battle?.host_stream_id;
  const myStreamIsOpp = stream?.id === battle?.opponent_stream_id;
  if (!battle || (!myStreamIsHost && !myStreamIsOpp)) {
    // Viewer's stream isn't in this battle. Rare fallback — render nothing.
    return null;
  }

  // The peek pane always covers the OPPOSITE half.
  //   Side "A" = host_stream_id     → LEFT half
  //   Side "B" = opponent_stream_id → RIGHT half
  const peekSide = myStreamIsHost ? "B" : "A";
  const peekTargetStreamId = myStreamIsHost ? battle.opponent_stream_id : battle.host_stream_id;
  const peekHandle = myStreamIsHost ? battle.opponent?.display_name : battle.host?.display_name;
  const peekPositionClass = peekSide === "A"
    ? "top-0 h-[var(--battle-arena-h)] left-0 w-1/2"
    : "top-0 h-[var(--battle-arena-h)] right-0 w-1/2";

  // Hosts of THIS battle use the participant-bridge endpoint; audience uses
  // the viewer-bridge endpoint. Both mint a subscribe-only LiveKit token
  // for the OTHER host's room.
  const currentUserIsBattleHost =
    currentUserId === battle?.host_id || currentUserId === battle?.opponent_id;

  return (
    <div
      data-testid="battle-split-layout"
      data-layout="left-right-50-50"
      className="absolute inset-0 pointer-events-none z-[15]"
    >
      {/* Opponent peek pane on the opposite half */}
      <div
        className={`absolute ${peekPositionClass} bg-black overflow-hidden pointer-events-auto`}
        data-testid={`battle-half-peek-${peekSide.toLowerCase()}`}
      >
        <BattleOpponentPane
          battleId={battle.id}
          targetStreamId={peekTargetStreamId}
          useHostBridge={currentUserIsBattleHost}
          audioBridgeReady={audioBridgeReady}
        />
        <PaneLabel side={peekSide} name={peekHandle} />
      </div>

      {/* Thin vertical center line — visual seam only. Faces stay clear. */}
      <div
        className="absolute left-1/2 top-0 h-[var(--battle-arena-h)] w-px -translate-x-1/2 bg-white/25 pointer-events-none z-[16]"
        data-testid="battle-split-center-line"
      />

      {/* Tiny VS badge centered on the split axis. Small enough to not
          obscure faces (~28px), sits below where the top BattleBanner ends
          so the two elements read as one battle-mode header + a mid-frame
          seam accent. */}
      <div
        className="absolute left-1/2 top-[calc(var(--battle-arena-h)/2)] -translate-x-1/2 -translate-y-1/2 pointer-events-none z-[17]"
        data-testid="battle-split-vs-badge"
      >
        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-rose-600 to-purple-700 border border-white/40 shadow-lg shadow-rose-500/40">
          <Swords size={14} className="text-white" />
        </div>
      </div>
    </div>
  );
}

/**
 * Small identity badge on the peek pane so each half is clearly labelled
 * "A · @handle" or "B · @handle". Positioned at the top-inner corner
 * of the peek half so it doesn't crowd the outer edge chips.
 */
function PaneLabel({ side, name }) {
  // Position inside the peek half at the top, offset from the center seam
  // and clear of the pane edges so it never rides the video border.
  const positionClass = side === "A"
    ? "top-2 right-3"   // A is the LEFT half → label at its inner (right) edge
    : "top-2 left-3";   // B is the RIGHT half → label at its inner (left) edge
  return (
    <div
      className={`absolute ${positionClass} z-20 flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-black/60 backdrop-blur-sm text-[10px] text-white/90 border border-white/10`}
      data-testid={`battle-pane-label-${side.toLowerCase()}`}
    >
      <span className="uppercase tracking-widest text-rose-200">{side}</span>
      <span className="truncate max-w-[100px]">@{name || "…"}</span>
    </div>
  );
}

/**
 * BattleOpponentPane — subscribe-only, hidden LiveKit connection to the
 * OTHER host's room, sized to fill the opposite half.
 *
 * Uses one of two endpoints depending on who the viewer is:
 *   - `/battles/{id}/bridge/token`         — host peeking into opponent
 *   - `/battles/{id}/viewer-bridge/token`  — audience peeking into either side
 */
function BattleOpponentPane({ battleId, targetStreamId, useHostBridge, audioBridgeReady }) {
  const [tokenInfo, setTokenInfo] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!battleId || !targetStreamId) return;
    let cancelled = false;
    (async () => {
      try {
        let data;
        if (useHostBridge) {
          const r = await api.post(`/battles/${battleId}/bridge/token`);
          data = r.data;
        } else {
          const r = await api.post(`/battles/${battleId}/viewer-bridge/token`, {
            target_stream_id: targetStreamId,
          });
          data = r.data;
        }
        if (!cancelled) setTokenInfo(data);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || "Opponent stream unavailable.");
      }
    })();
    return () => { cancelled = true; };
  }, [battleId, targetStreamId, useHostBridge]);

  if (err) {
    return (
      <div className="absolute inset-0 flex items-center justify-center text-[11px] text-rose-300 bg-black">
        Opponent unavailable
      </div>
    );
  }
  if (!tokenInfo?.token) {
    return (
      <div className="absolute inset-0 flex items-center justify-center text-[11px] text-zinc-500 bg-black">
        Connecting to opponent…
      </div>
    );
  }
  return (
    <LiveKitRoom
      key={tokenInfo.token}
      token={tokenInfo.token}
      serverUrl={tokenInfo.server_url}
      options={PEEK_ROOM_OPTIONS}
      connect
      audio={false}
      video={false}
      data-lk-theme="default"
      style={{ height: "100%", width: "100%", background: "#000" }}
    >
      {/* Iter68 — when the server audio bridge is "ready", the opponent's
          audio is delivered natively via the main LiveRoomStage's room.
          Playing it a SECOND time from this peek connection would cause
          the very double-audio race we're trying to eliminate. So we
          only render <RoomAudioRenderer /> when the bridge is NOT ready
          (legacy fallback path). */}
      {!audioBridgeReady && <RoomAudioRenderer />}
      <OpponentHalfVideo />
    </LiveKitRoom>
  );
}

function OpponentHalfVideo() {
  const trackRefs = useTracks(
    [{ source: Track.Source.Camera, withPlaceholder: false }],
    { onlySubscribed: true }
  );
  const opp = useMemo(() => {
    return trackRefs.find((t) => {
      const pid = t.participant?.identity || "";
      if (pid.startsWith("battle-peek-") || pid.startsWith("battle-viewer-peek-")) return false;
      // Iter68 — the audio-bridge bot connects as `battle-bridge-{battle_id}`
      // in this same room and publishes an audio-only track. Filter it
      // out of the video-track candidate list so `useTracks` never picks
      // the bot as the opponent video.
      if (pid.startsWith("battle-bridge-")) return false;
      const perms = t.participant?.permissions;
      if (perms && perms.canPublish === false) return false;
      return true;
    });
  }, [trackRefs]);

  useEffect(() => {
    const pub = opp?.publication;
    if (!pub || !pub.setVideoQuality) return;
    try { pub.setVideoQuality(VideoQuality.LOW); }
    catch (err) { console.warn("[BattleSplitLayout] setVideoQuality failed:", err); }
  }, [opp?.publication]);

  if (!opp) {
    return (
      <div className="absolute inset-0 flex items-center justify-center text-[11px] text-zinc-500">
        Waiting for opponent…
      </div>
    );
  }
  return (
    <div className="absolute inset-0">
      <ParticipantTile trackRef={opp} disableSpeakingIndicator />
    </div>
  );
}
